import { CommonModule, DOCUMENT } from '@angular/common';
import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	ViewChild,
	OnInit,
	OnDestroy,
	signal,
	computed,
	effect,
	inject,
	TemplateRef,
	ViewContainerRef,
	forwardRef
} from '@angular/core';
import {
	FormControl,
	FormsModule,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ControlValueAccessor
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { DatepickerService } from './datepicker.service';
import { TemplatePortal } from '@angular/cdk/portal';
import {
	Overlay,
	OverlayRef,
	OverlayModule,
	FlexibleConnectedPositionStrategy,
	ConnectedPosition
} from '@angular/cdk/overlay';
import { DateMaskDirective } from './date-mask.directive';

@Component({
	selector: 'datepicker',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		DateMaskDirective,
		OverlayModule
	],
	templateUrl: './datepicker.component.html',
	styleUrl: './datepicker.component.css',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DatepickerComponent),
			multi: true
		}
	]
})
export class DatepickerComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	@Input() minDate?: Date;
	@Input() maxDate?: Date;
	@Input() dateFormat: string = 'mm/dd/yyyy';
	@Input() placeHolder: string = 'Select date';
	@Input() label = '';
	@Input() showFloatingLabel = false;
	@Input() isRequired = false;
	@Input() isError = false;
	@Input() disabled = false;
	@Input() inputClass = '';
	// @Input() class = ''; // Removed to support standard host styling
	@Input() allowManualInput = true;
	@Input() enableDateMask = true;
	@Input() autofocus = false;
	@Output() onClearSelection = new EventEmitter<void>();

	@ViewChild('datepickerRef') datepickerRef!: ElementRef;
	@ViewChild('calendarContainer') calendarContainer!: ElementRef;
	@ViewChild('calendarTemplate') calendarTemplate!: TemplateRef<any>;

	// Service injection for coordinating multiple datepicker instances
	private readonly datepickerService = inject(DatepickerService);
	private readonly overlay = inject(Overlay);
	private readonly viewContainerRef = inject(ViewContainerRef);
	private readonly instanceId = `datepicker-${Math.random().toString(36).substr(2, 9)}`;
	private serviceSubscription?: Subscription;
	private overlayRef: OverlayRef | null = null;

	// Signals for reactive state
	dateControl = new FormControl('');
	isOpen = signal(false);
	selectedDate = signal<Date | null>(null);
	currentDate = signal(new Date());
	showYearSelect = signal(false);
	showMonthSelect = signal(false);
	yearDecadeStart = signal(0);
	validationError = signal<string>('');
	isIconFocused = signal(false);

	// Positioning signals for smart calendar placement (kept for backwards compatibility)
	calendarPosition = signal<{
		top?: string;
		bottom?: string;
		left?: string;
		right?: string;
	}>({});

	// Computed signals
	today = computed(() => new Date());
	weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	months = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December'
	];

	abbreviatedMonths = computed(() =>
		this.months.map((month) => month.substring(0, 3))
	);

	calendarDays = computed(() => {
		const date = this.currentDate();
		const year = date.getFullYear();
		const month = date.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const days: Date[] = [];

		// Add days from previous month
		for (let i = firstDay.getDay(); i > 0; i--) {
			const prevDate = new Date(year, month, 1 - i);
			days.push(prevDate);
		}

		// Add days of current month
		for (let i = 1; i <= lastDay.getDate(); i++) {
			days.push(new Date(year, month, i));
		}

		// Add days from next month
		const remainingDays = 42 - days.length; // 6 rows × 7 days
		for (let i = 1; i <= remainingDays; i++) {
			days.push(new Date(year, month + 1, i));
		}

		return days;
	});

	// Year selection with decades
	yearDecade = computed(() => {
		const start = this.yearDecadeStart();
		const years: number[] = [];
		for (let i = 0; i < 12; i++) {
			years.push(start + i);
		}
		return years;
	});

	// Flag to track programmatic changes
	private isProgrammaticChange = false;

	// ControlValueAccessor implementation
	private onChange: any = () => {};
	private onTouched: any = () => {};

	constructor() {
		// Initialize year decade to current year
		const currentYear = new Date().getFullYear();
		this.yearDecadeStart.set(Math.floor(currentYear / 10) * 10);

		// Set default min/max dates if not provided
		if (!this.minDate) {
			this.minDate = new Date(currentYear - 100, 0, 1);
		}
		if (!this.maxDate) {
			this.maxDate = new Date(currentYear + 100, 11, 31);
		}

		// Effect to update input display when selected date changes
		// Note: This effect should ONLY update the UI, NOT call onChange
		// onChange is called explicitly in user-initiated actions (selectDate, input blur, etc.)
		effect(() => {
			const date = this.selectedDate();
			if (date && !this.isProgrammaticChange) {
				const formatted = this.formatDate(date);
				this.dateControl.setValue(formatted);
				// DO NOT call onChange here - it causes forms to be marked dirty on load
				// onChange is called explicitly in selectDate() and autoCompleteYear()
			}
		});
	}

	ngOnInit() {
		this.setupClickOutside();
		this.setupScrollListener();
		this.setupServiceSubscription();
	}

	private setupServiceSubscription() {
		// Subscribe to close events from other datepicker instances
		this.serviceSubscription =
			this.datepickerService.onCloseOthers$.subscribe((activeId) => {
				// Close this datepicker if another one became active
				if (activeId !== this.instanceId && this.isOpen()) {
					this.closeCalendar();
				}
			});
	}

	// ControlValueAccessor methods
	writeValue(value: any): void {
		this.isProgrammaticChange = true;
		if (value) {
			let date: Date | null = null;

			// Handle Date object directly
			if (value instanceof Date && !isNaN(value.getTime())) {
				date = value;
			}
			// Handle string value - try to parse it
			else if (typeof value === 'string') {
				date = this.parseDate(value);

				// If string parsing failed, try to create Date from string (ISO format, etc.)
				if (!date) {
					const parsedDate = new Date(value);
					if (!isNaN(parsedDate.getTime())) {
						date = parsedDate;
					}
				}
			}
			// Handle number (timestamp)
			else if (typeof value === 'number') {
				const parsedDate = new Date(value);
				if (!isNaN(parsedDate.getTime())) {
					date = parsedDate;
				}
			}

			if (date) {
				this.selectedDate.set(date);
				this.currentDate.set(
					new Date(date.getFullYear(), date.getMonth())
				);
				this.dateControl.setValue(this.formatDate(date));
			} else {
				this.selectedDate.set(null);
				this.dateControl.setValue('');
			}
		} else {
			this.selectedDate.set(null);
			this.dateControl.setValue('');
		}
		this.isProgrammaticChange = false;
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	private readonly handleClickOutside = (event: MouseEvent) => {
		if (!this.isOpen()) return;

		const clickedInsideInput = this.datepickerRef?.nativeElement?.contains(
			event.target
		);
		const clickedInsideCalendar =
			this.calendarContainer?.nativeElement?.contains(event.target);

		if (!clickedInsideInput && !clickedInsideCalendar) {
			this.closeCalendar();
		}
	};

	private scrollRAFId: number | null = null;
	private resizeRAFId: number | null = null;

	private readonly handleScroll = () => {
		if (this.isOpen()) {
			// Cancel any pending animation frame to avoid stacking
			if (this.scrollRAFId) {
				cancelAnimationFrame(this.scrollRAFId);
			}
			// Use requestAnimationFrame for smooth repositioning during scroll
			this.scrollRAFId = requestAnimationFrame(() => {
				this.calculateCalendarPosition();
				this.scrollRAFId = null;
			});
		}
	};

	private readonly handleResize = () => {
		if (this.isOpen()) {
			// Cancel any pending animation frame
			if (this.resizeRAFId) {
				cancelAnimationFrame(this.resizeRAFId);
			}
			// Use requestAnimationFrame for smooth repositioning during resize
			this.resizeRAFId = requestAnimationFrame(() => {
				this.calculateCalendarPosition();
				this.resizeRAFId = null;
			});
		}
	};

	private setupClickOutside() {
		document.addEventListener('click', this.handleClickOutside);
	}

	private setupScrollListener() {
		// Listen to scroll on window and any scrollable parent
		window.addEventListener('scroll', this.handleScroll, true);
		window.addEventListener('resize', this.handleResize);
	}

	private removeScrollListener() {
		window.removeEventListener('scroll', this.handleScroll, true);
		window.removeEventListener('resize', this.handleResize);
	}

	private closeCalendar() {
		if (!this.isOpen()) return;

		// Detach from overlay
		if (this.overlayRef?.hasAttached()) {
			this.overlayRef.detach();
		}

		this.isOpen.set(false);
		this.showYearSelect.set(false);
		this.showMonthSelect.set(false);
		this.validateInput();
		this.datepickerService.clearActive(this.instanceId);
	}

	toggleCalendar(event: Event) {
		if (this.disabled) return;
		event.stopPropagation();
		const newState = !this.isOpen();

		if (newState) {
			this.openCalendar();
		} else {
			this.closeCalendar();
		}
	}

	private openCalendar() {
		if (this.isOpen()) return;

		// Notify service to close other datepickers
		this.datepickerService.setActive(this.instanceId);

		// Dispose existing overlay to recalculate position based on current viewport
		this.disposeOverlay();

		// Create new overlay with fresh position calculation
		const overlayRef = this.createOverlay();

		// Attach the template to the overlay
		if (!overlayRef.hasAttached()) {
			const portal = new TemplatePortal(
				this.calendarTemplate,
				this.viewContainerRef
			);
			overlayRef.attach(portal);
		}

		this.isOpen.set(true);
	}

	private disposeOverlay() {
		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
	}

	private createOverlay(): OverlayRef {
		const positionStrategy = this.getPositionStrategy();

		this.overlayRef = this.overlay.create({
			positionStrategy,
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
			hasBackdrop: false,
			panelClass: 'datepicker-overlay-panel'
		});

		// Handle clicks outside to close
		this.overlayRef.outsidePointerEvents().subscribe((event) => {
			const target = event.target as HTMLElement;
			// Don't close if clicking on the datepicker input
			if (!this.datepickerRef?.nativeElement?.contains(target)) {
				this.closeCalendar();
			}
		});

		return this.overlayRef;
	}

	private getPositionStrategy(): FlexibleConnectedPositionStrategy {
		// Calendar height is approximately 340px - use this to determine best position
		const calendarHeight = 340;

		// Get input element position to determine best initial position
		const inputElement = this.datepickerRef?.nativeElement;
		const inputRect = inputElement?.getBoundingClientRect();
		const viewportHeight = window.innerHeight;

		// Calculate available space
		const spaceBelow = inputRect
			? viewportHeight - inputRect.bottom
			: viewportHeight / 2;
		const spaceAbove = inputRect ? inputRect.top : viewportHeight / 2;

		// Determine if we should prefer opening above or below
		const preferAbove =
			spaceBelow < calendarHeight && spaceAbove > spaceBelow;

		const positionsBelow: ConnectedPosition[] = [
			// Below the input, start-aligned
			{
				originX: 'start',
				originY: 'bottom',
				overlayX: 'start',
				overlayY: 'top',
				offsetY: 4
			},
			// Below the input, end-aligned
			{
				originX: 'end',
				originY: 'bottom',
				overlayX: 'end',
				overlayY: 'top',
				offsetY: 4
			}
		];

		const positionsAbove: ConnectedPosition[] = [
			// Above the input, start-aligned
			{
				originX: 'start',
				originY: 'top',
				overlayX: 'start',
				overlayY: 'bottom',
				offsetY: -4
			},
			// Above the input, end-aligned
			{
				originX: 'end',
				originY: 'top',
				overlayX: 'end',
				overlayY: 'bottom',
				offsetY: -4
			}
		];

		// Order positions based on available space
		const positions: ConnectedPosition[] = preferAbove
			? [...positionsAbove, ...positionsBelow]
			: [...positionsBelow, ...positionsAbove];

		return this.overlay
			.position()
			.flexibleConnectedTo(this.datepickerRef)
			.withPositions(positions)
			.withPush(true)
			.withFlexibleDimensions(false)
			.withViewportMargin(8);
	}

	onInputClick(event: Event) {
		if (this.disabled || !this.allowManualInput) return;
		event.stopPropagation();
		// Just focus the input, don't open calendar
	}

	onIconFocus(event: Event) {
		if (this.disabled) return;
		this.isIconFocused.set(true);
	}

	onIconBlur(event: Event) {
		if (this.disabled) return;
		this.isIconFocused.set(false);
	}

	onTabKey(event: Event) {
		if (!this.allowManualInput) return;
		// Auto-complete is now handled by the directive on blur/input
	}

	onInputBlur(event: Event) {
		if (!this.allowManualInput) return;

		const input = event.target as HTMLInputElement;
		const value = input.value.trim();

		if (!value) {
			this.validationError.set('');
			return;
		}

		// Validate complete date input
		this.validateInput();
	}

	onInputChange(event: Event) {
		if (!this.allowManualInput) return;

		const input = event.target as HTMLInputElement;
		const value = input.value;

		// Clear validation error while typing
		this.validationError.set('');

		// Try to parse the date if input is complete
		if (value.length === this.dateFormat.length) {
			const date = this.parseDate(value);
			if (date) {
				if (this.isDateDisabled(date)) {
					this.validationError.set('Date is outside allowed range');
				} else {
					this.selectedDate.set(date);
					this.validationError.set('');
					this.onChange(value); // Notify form of user-initiated change
				}
			} else {
				this.validationError.set('Invalid date format');
			}
		}
	}

	private validateInput() {
		const value = this.dateControl.value;
		if (!value) {
			this.validationError.set('');
			return;
		}

		const date = this.parseDate(value);
		if (!date) {
			this.validationError.set('Invalid date format');
			this.dateControl.setValue('');
			this.selectedDate.set(null);
		} else if (this.isDateDisabled(date)) {
			this.validationError.set('Date is outside allowed range');
			this.dateControl.setValue('');
			this.selectedDate.set(null);
		} else {
			this.validationError.set('');
		}
	}

	selectDate(date: Date) {
		if (this.isDateDisabled(date)) return;

		const newDate = new Date(date);
		this.selectedDate.set(newDate);
		const formatted = this.formatDate(newDate);
		this.dateControl.setValue(formatted);
		this.onChange(formatted); // Notify form of user-initiated change
		this.onTouched();
		this.isOpen.set(false);
		this.validationError.set('');

		// Close the overlay
		if (this.overlayRef?.hasAttached()) {
			this.overlayRef.detach();
		}

		// Restore focus to input field after closing calendar to maintain tab navigation
		setTimeout(() => {
			const inputElement =
				this.datepickerRef?.nativeElement?.querySelector(
					'input[type="text"]'
				) as HTMLInputElement;
			if (inputElement) {
				inputElement.focus();
			}
		}, 0);
	}

	isPrevDisabled(): boolean {
		if (!this.minDate) return false;
		const prevMonthDate = new Date(
			this.currentDate().getFullYear(),
			this.currentDate().getMonth() - 1
		);
		return prevMonthDate < this.minDate;
	}

	isNextDisabled(): boolean {
		if (!this.maxDate) return false;
		const nextMonthDate = new Date(
			this.currentDate().getFullYear(),
			this.currentDate().getMonth() + 1
		);
		return nextMonthDate > this.maxDate;
	}

	prevMonth() {
		const current = this.currentDate();
		this.currentDate.set(
			new Date(current.getFullYear(), current.getMonth() - 1)
		);
	}

	nextMonth() {
		const current = this.currentDate();
		this.currentDate.set(
			new Date(current.getFullYear(), current.getMonth() + 1)
		);
	}

	toggleYearSelect(event: Event) {
		event.stopPropagation();
		this.showYearSelect.set(!this.showYearSelect());
		this.showMonthSelect.set(false);

		// Center decade on current year
		if (this.showYearSelect()) {
			const currentYear = this.currentDate().getFullYear();
			this.yearDecadeStart.set(Math.floor(currentYear / 10) * 10);
		}
	}

	toggleMonthSelect(event: Event) {
		event.stopPropagation();
		this.showMonthSelect.set(!this.showMonthSelect());
		this.showYearSelect.set(false);
	}

	selectYear(event: Event, year: number) {
		event.stopPropagation();
		const current = this.currentDate();
		this.currentDate.set(new Date(year, current.getMonth()));
		this.showYearSelect.set(false);
		// Open month picker after selecting year
		this.showMonthSelect.set(true);
	}

	selectMonth(event: Event, monthIndex: number) {
		event.stopPropagation();
		const current = this.currentDate();
		this.currentDate.set(new Date(current.getFullYear(), monthIndex));
		this.showMonthSelect.set(false);
	}

	prevDecade(event: Event) {
		event.stopPropagation();
		this.yearDecadeStart.set(this.yearDecadeStart() - 10);
	}

	nextDecade(event: Event) {
		event.stopPropagation();
		this.yearDecadeStart.set(this.yearDecadeStart() + 10);
	}

	jumpToCurrentYear(event: Event) {
		event.stopPropagation();
		const now = new Date();
		const currentYear = now.getFullYear();
		this.yearDecadeStart.set(Math.floor(currentYear / 10) * 10);
		// Update current date to current year
		this.currentDate.set(new Date(currentYear, now.getMonth()));
	}

	jumpToCurrentMonth(event: Event) {
		event.stopPropagation();
		const now = new Date();
		this.currentDate.set(new Date(now.getFullYear(), now.getMonth()));
		this.showMonthSelect.set(false);
	}

	isDateDisabled(date: Date): boolean {
		if (
			this.minDate &&
			date < new Date(this.minDate.setHours(0, 0, 0, 0))
		) {
			return true;
		}
		if (
			this.maxDate &&
			date > new Date(this.maxDate.setHours(23, 59, 59, 999))
		) {
			return true;
		}
		return false;
	}

	isSameDay(date1: Date, date2: Date | null): boolean {
		if (!date2) return false;
		return (
			date1.getDate() === date2.getDate() &&
			date1.getMonth() === date2.getMonth() &&
			date1.getFullYear() === date2.getFullYear()
		);
	}

	getDayClass(day: Date): string[] {
		const baseClass =
			'flex h-[30px] w-[30px] items-center justify-center text-sm';
		const currentMonth = day.getMonth() === this.currentDate().getMonth();
		const isDisabled = this.isDateDisabled(day);
		const isToday = this.isSameDay(day, this.today());
		const isSelected = this.isSameDay(day, this.selectedDate());

		let classes = [baseClass];

		if (isDisabled) {
			classes.push('cursor-not-allowed', 'opacity-50');
		} else {
			classes.push(
				'cursor-pointer',
				'hover:bg-gray-100',
				'dark:hover:bg-gray-700'
			);
		}

		if (!currentMonth) {
			classes.push('text-gray-400', 'dark:text-gray-600');
		} else if (!isToday && !isSelected) {
			classes.push('text-gray-700', 'dark:text-gray-300');
		}

		if (isSelected) {
			classes.push('!bg-blue-600', 'text-white', 'rounded-full');
		} else if (isToday) {
			classes.push(
				'bg-gray-500',
				'dark:bg-gray-600',
				'border',
				'border-lime-300',
				'text-white',
				'rounded-full'
			);
		}

		return classes;
	}

	clearDate(event?: Event) {
		if (event) {
			event.stopPropagation();
		}
		this.selectedDate.set(null);
		this.dateControl.setValue('');
		this.validationError.set('');
		this.onChange('');
		this.onClearSelection.emit();
	}

	private formatDate(date: Date): string {
		if (!date) return '';

		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();

		// Format based on dateFormat pattern
		return this.dateFormat
			.replace('dd', day)
			.replace('mm', month)
			.replace('yyyy', String(year));
	}

	private parseDate(value: string): Date | null {
		if (!value) return null;

		// Get separator from format
		const separator = this.dateFormat.includes('/') ? '/' : '-';
		const parts = value.split(separator);
		const formatParts = this.dateFormat.split(separator);

		if (parts.length !== formatParts.length) return null;

		let day = 0,
			month = 0,
			year = 0;

		for (let i = 0; i < formatParts.length; i++) {
			const part = formatParts[i];
			const val = parseInt(parts[i], 10);

			if (isNaN(val)) return null;

			if (part === 'dd') day = val;
			else if (part === 'mm') month = val;
			else if (part === 'yyyy') year = val;
		}

		// Validate
		if (month < 1 || month > 12) return null;
		if (day < 1 || day > 31) return null;
		if (year < 1000 || year > 9999) return null;

		const date = new Date(year, month - 1, day);

		// Check if date is valid (e.g., Feb 30 would roll over to March)
		if (
			date.getDate() !== day ||
			date.getMonth() !== month - 1 ||
			date.getFullYear() !== year
		) {
			return null;
		}

		return date;
	}

	/**
	 * Calculate smart positioning for calendar dropdown based on viewport
	 * Uses fixed positioning to avoid overflow clipping issues
	 */
	private calculateCalendarPosition() {
		if (!this.datepickerRef?.nativeElement) return;

		// Get the datepicker container element's bounding rect
		const containerRect =
			this.datepickerRef.nativeElement.getBoundingClientRect();

		// Also try to find the input element for more precise positioning
		const inputElement =
			this.datepickerRef.nativeElement.querySelector(
				'input[type="text"]'
			);
		const inputRect = inputElement
			? inputElement.getBoundingClientRect()
			: containerRect;

		// Validate that we have valid dimensions (element is visible)
		if (inputRect.width === 0 && inputRect.height === 0) {
			// Element not visible, use container rect or defer
			if (containerRect.width === 0 && containerRect.height === 0) {
				return; // Neither element is visible, skip positioning
			}
		}

		// Use the rect that has valid dimensions
		const rect =
			inputRect.width > 0 && inputRect.height > 0
				? inputRect
				: containerRect;

		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;

		// Calendar dimensions
		const calendarHeight = 340; // Approximate height of calendar
		const calendarWidth = 280; // Width from HTML
		const gap = 4; // Small gap between input and calendar
		const padding = 8; // Minimum padding from viewport edges

		const position: {
			top?: string;
			bottom?: string;
			left?: string;
			right?: string;
		} = {};

		// Vertical positioning
		const spaceBelow = viewportHeight - rect.bottom;
		const spaceAbove = rect.top;

		let topPosition: number;

		if (spaceBelow >= calendarHeight + gap) {
			// Position below the input - enough space
			topPosition = rect.bottom + gap;
		} else if (spaceAbove >= calendarHeight + gap) {
			// Position above the input - enough space above
			topPosition = rect.top - calendarHeight - gap;
		} else {
			// Not enough space above or below - use scrollable positioning
			// Position where there's more space
			if (spaceBelow >= spaceAbove) {
				// Position below, constrained to viewport
				topPosition = Math.min(
					rect.bottom + gap,
					viewportHeight - calendarHeight - padding
				);
			} else {
				// Position above, constrained to viewport
				topPosition = Math.max(
					padding,
					rect.top - calendarHeight - gap
				);
			}
		}

		// Final bounds check for top position
		topPosition = Math.max(
			padding,
			Math.min(topPosition, viewportHeight - calendarHeight - padding)
		);
		position.top = `${topPosition}px`;

		// Horizontal positioning
		let leftPosition: number;

		// On small screens (mobile), center the calendar
		if (viewportWidth < 400) {
			leftPosition = (viewportWidth - calendarWidth) / 2;
		} else {
			// Align with input left edge, accounting for input width
			const inputCenter = rect.left + rect.width / 2;
			const calendarCenter = calendarWidth / 2;

			// Try to center calendar under input if input is narrower
			if (rect.width < calendarWidth) {
				leftPosition = inputCenter - calendarCenter;
			} else {
				// Align with input left edge for wider inputs
				leftPosition = rect.left;
			}

			// Ensure calendar stays within viewport bounds
			if (leftPosition + calendarWidth > viewportWidth - padding) {
				leftPosition = viewportWidth - calendarWidth - padding;
			}
			if (leftPosition < padding) {
				leftPosition = padding;
			}
		}

		position.left = `${leftPosition}px`;

		this.calendarPosition.set(position);
	}

	ngOnDestroy() {
		document.removeEventListener('click', this.handleClickOutside);
		this.removeScrollListener();
		this.serviceSubscription?.unsubscribe();
		this.datepickerService.clearActive(this.instanceId);

		// Clean up any pending animation frames
		if (this.scrollRAFId) {
			cancelAnimationFrame(this.scrollRAFId);
		}
		if (this.resizeRAFId) {
			cancelAnimationFrame(this.resizeRAFId);
		}

		// Clean up overlay
		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = null;
		}
	}
}
